/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Loader } from '@googlemaps/js-api-loader';
import ErrorPopup from "./ErrorPopup";
import ours from '@oursprivacy/cdp-sdk';

// Declare google as a global variable to avoid linter errors
/* global google */

const AddressQuestion = ({ onSubmit, onNext, initialValue, user }) => {
  const [address, setAddress] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [addressComponents, setAddressComponents] = useState(null);

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const openError = useCallback((msg) => {
    setErrorMessage(msg);
    setErrorOpen(true);
  }, []);
  const closeError = useCallback(() => setErrorOpen(false), []);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Validate address completeness
  const validateAddress = useCallback((addr, components) => {
    if (!addr || !addr.trim()) {
      return false;
    }

    // If we have address components from Google Places, validate them
    if (components && Array.isArray(components)) {
      const hasStreetNumber = components.some(c => 
        c.types.includes('street_number')
      );
      const hasRoute = components.some(c => 
        c.types.includes('route')
      );
      const hasCity = components.some(c => 
        c.types.includes('locality') || c.types.includes('sublocality')
      );
      const hasState = components.some(c => 
        c.types.includes('administrative_area_level_1')
      );
      const hasZip = components.some(c => 
        c.types.includes('postal_code')
      );

      // Require at least street number/route, city, state, and zip for a complete address
      return hasStreetNumber && hasRoute && hasCity && hasState && hasZip;
    }

    // Fallback: validate address string has minimum required parts
    // A complete US address should have: street number, street name, city, state, zip
    const addressParts = addr.split(',').map(part => part.trim());
    
    // Should have at least 3 parts (street, city, state zip)
    if (addressParts.length < 3) {
      return false;
    }

    // Check for zip code (5 digits or 5+4 format)
    const zipPattern = /\b\d{5}(-\d{4})?\b/;
    if (!zipPattern.test(addr)) {
      return false;
    }

    // Check for state abbreviation or full state name
    const statePattern = /\b([A-Z]{2}|Alabama|Alaska|Arizona|Arkansas|California|Colorado|Connecticut|Delaware|Florida|Georgia|Hawaii|Idaho|Illinois|Indiana|Iowa|Kansas|Kentucky|Louisiana|Maine|Maryland|Massachusetts|Michigan|Minnesota|Mississippi|Missouri|Montana|Nebraska|Nevada|New Hampshire|New Jersey|New Mexico|New York|North Carolina|North Dakota|Ohio|Oklahoma|Oregon|Pennsylvania|Rhode Island|South Carolina|South Dakota|Tennessee|Texas|Utah|Vermont|Virginia|Washington|West Virginia|Wisconsin|Wyoming)\b/i;
    if (!statePattern.test(addr)) {
      return false;
    }

    // Check for street number (starts with a number)
    const streetNumberPattern = /^\d+/;
    if (!streetNumberPattern.test(addressParts[0])) {
      return false;
    }

    return true;
  }, []);

  // Set initial value when editing
  useEffect(() => {
    if (initialValue) {
      setAddress(initialValue);
      // If initial value exists, assume it's valid (from previous submission)
      setIsAddressValid(true);
    }
  }, [initialValue]);

  // Initialize Google Places API
  useEffect(() => {
    const initializeGooglePlaces = async () => {
      try {
        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
          version: "weekly",
          libraries: ["places"]
        });

        const google = await loader.load();

        // Create services
        const autocomplete = new google.maps.places.AutocompleteService();
        const places = new google.maps.places.PlacesService(
          document.createElement('div')
        );

        setAutocompleteService(autocomplete);
        setPlacesService(places);
      } catch (error) {
        console.error('Error loading Google Places API:', error);
        // Fallback: disable autocomplete if API key is not available
      }
    };

    initializeGooglePlaces();
  }, [openError]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    const previousAddress = address;
    setAddress(value);
    
    // If user manually edits the address (not selecting from suggestions),
    // clear the address components and reset validation
    // This ensures we re-validate the manually entered address
    if (value !== previousAddress && addressComponents) {
      setAddressComponents(null);
      setIsAddressValid(false);
    }

    if (value.length > 2 && autocompleteService) {
      setIsLoading(true);

      autocompleteService.getPlacePredictions(
        {
          input: value,
          types: ['address'],
          componentRestrictions: { country: 'us' } // Restrict to US addresses
        },
        (predictions, status) => {
          setIsLoading(false);
          if (status === window.google?.maps?.places?.PlacesServiceStatus?.OK && predictions) {
            setSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    setAddress(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);

    // Get place details for more complete address
    if (placesService) {
      placesService.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ['formatted_address', 'address_components']
        },
        (place, status) => {
          if (status === window.google?.maps?.places?.PlacesServiceStatus?.OK && place) {
            const formattedAddress = place.formatted_address;
            const components = place.address_components;
            
            setAddress(formattedAddress);
            setAddressComponents(components);
            
            // Validate the address with components
            const isValid = validateAddress(formattedAddress, components);
            setIsAddressValid(isValid);
          }
        }
      );
    } else {
      // If placesService is not available, validate with string only
      const isValid = validateAddress(suggestion.description, null);
      setIsAddressValid(isValid);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!address.trim()) {
      openError("Please enter your address.");
      return;
    }

    if (!isAddressValid) {
      openError("Please select a complete address from the suggestions, or ensure your address includes street number, street name, city, state, and zip code.");
      return;
    }

    onSubmit(address);

    // Track events with ours
    if (user?.email && user?.phoneNo) {
      ours.track('AddressEntry', {
        email: user.email,
        phone_number: user.phoneNo,
        custom_properties: {
          source: 'conversational intake'
        }
      });

      ours.track('Subscription', {
        value: 119,
        currency: 'USD',
        custom_properties: {
          source: 'conversational intake'
        }
      }, {
        email: user.email,
        phone_number: user.phoneNo
      });
    }

    onNext();

    // Reset form
    setAddress("");
    setSuggestions([]);
    setShowSuggestions(false);
    setIsAddressValid(false);
    setAddressComponents(null);
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Validate address on change (for manual entry)
  useEffect(() => {
    if (address && !addressComponents) {
      // Only validate if user hasn't selected from suggestions
      // This allows manual entry but requires it to be complete
      const isValid = validateAddress(address, null);
      setIsAddressValid(isValid);
    }
  }, [address, addressComponents, validateAddress]);

  const canSubmit = address.trim() !== "" && isAddressValid;

  return (
    <>
      <form onSubmit={handleSubmit} className="input-form">
        <div className="address-container">
          <div className="address-instructions">
            <p>Please enter your complete address for billing and service purposes.</p>
          </div>

          <div className="address-input-container">
            <label htmlFor="address-input">Address</label>
            <div className="address-input-wrapper">
              <input
                ref={inputRef}
                id="address-input"
                type="text"
                value={address}
                onChange={handleInputChange}
                placeholder="Start typing your address..."
                className="address-input"
                autoComplete="off"
              />
              {isLoading && (
                <div className="address-loading">
                  <div className="loading-spinner"></div>
                </div>
              )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
              <div ref={suggestionsRef} className="address-suggestions">
                {suggestions.map((suggestion) => (
                  <div
                    key={suggestion.place_id}
                    className="address-suggestion-item"
                    onClick={() => handleSuggestionSelect(suggestion)}
                  >
                    <div className="suggestion-main-text">
                      {suggestion.structured_formatting.main_text}
                    </div>
                    <div className="suggestion-secondary-text">
                      {suggestion.structured_formatting.secondary_text}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            className="address-submit-button"
            disabled={!canSubmit}
          >
            Continue
          </button>
        </div>
      </form>

      {/* Error modal */}
      <ErrorPopup open={errorOpen} message={errorMessage} onClose={closeError} />
    </>
  );
};

export default AddressQuestion;
